/**
 * Web implementation of `@react-native-community/datetimepicker`.
 *
 * Consumer: src/shared/ui/time-field.tsx, which mounts the picker only while
 * `open` is true and closes on the first callback — `event.type === 'set'`
 * commits the value, 'dismissed' leaves it untouched. This mirrors that
 * contract with the browser's own `<input type="time">`, which brings the same
 * two properties the native dial was chosen for: no locale-parsing of typed
 * "HH:MM" text, and no way to enter 25:70.
 *
 * `showPicker()` opens the dial immediately on Chromium and Safari so a tap
 * behaves like the native modal. Firefox has no such call and opens on focus
 * instead, so focusing covers both.
 */
import React, {useEffect, useRef} from 'react';

function pad(n) {
  return String(n).padStart(2, '0');
}

function inputTypeFor(mode) {
  if (mode === 'time') {
    return 'time';
  }
  return mode === 'datetime' ? 'datetime-local' : 'date';
}

function formatValue(date, mode) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (mode === 'time') {
    return time;
  }
  return mode === 'datetime' ? `${day}T${time}` : day;
}

/**
 * Applies the input's string back onto a copy of `base`, so the fields this
 * picker does not cover (the date half of a time picker, seconds, ms) keep what
 * the caller passed in instead of silently jumping to "now".
 */
function merge(base, raw, mode) {
  const next = new Date(
    base instanceof Date && !Number.isNaN(base.getTime())
      ? base.getTime()
      : Date.now(),
  );

  let datePart = null;
  let timePart = null;
  if (mode === 'time') {
    timePart = raw;
  } else if (mode === 'datetime') {
    [datePart, timePart] = raw.split('T');
  } else {
    datePart = raw;
  }

  if (datePart) {
    const [y, m, d] = datePart.split('-').map(Number);
    next.setFullYear(y, m - 1, d);
  }
  if (timePart) {
    const [h, min] = timePart.split(':').map(Number);
    next.setHours(h, min, 0, 0);
  }
  return next;
}

export default function DateTimePicker({value, mode = 'date', onChange}) {
  const ref = useRef(null);
  // The caller unmounts us on the first callback, so a second one would fire
  // against a dead component — and blur races the change event when the user
  // picks a time and clicks away in one motion.
  const settled = useRef(false);
  // Focusing on mount can itself provoke a blur if anything grabs focus in the
  // same tick; without this the picker would close the instant it opened.
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }
    el.focus({preventScroll: true});
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
      } catch {
        // Some browsers only allow this inside a user gesture; focus already
        // opens the dial there, so a failure here is not worth surfacing.
      }
    }
    const timer = setTimeout(() => {
      armed.current = true;
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const settle = (type, date) => {
    if (settled.current) {
      return;
    }
    settled.current = true;
    onChange?.({type}, date);
  };

  return React.createElement('input', {
    ref,
    type: inputTypeFor(mode),
    defaultValue: formatValue(value, mode),
    onChange: event => {
      const raw = event.target.value;
      // An emptied field is not a value — treat it as a dismissal so the caller
      // keeps whatever it had.
      if (!raw) {
        settle('dismissed');
        return;
      }
      settle('set', merge(value, raw, mode));
    },
    onBlur: () => {
      if (armed.current) {
        settle('dismissed');
      }
    },
    onKeyDown: event => {
      if (event.key === 'Escape') {
        settle('dismissed');
      }
    },
    style: {
      marginTop: 6,
      padding: '10px 12px',
      borderRadius: 8,
      border: '1px solid rgba(127,127,127,0.45)',
      background: 'transparent',
      color: 'inherit',
      font: 'inherit',
      width: '100%',
      boxSizing: 'border-box',
    },
  });
}
